/**
 * An organization in the record panel: status, partner, contact, dates,
 * the tenant's counts as rows into its workspace, and Open workspace.
 */
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { listAgents } from '@optum/api-client/endpoints/agents';
import { listOrgCollections } from '@optum/api-client/endpoints/collections';
import { listGroups } from '@optum/api-client/endpoints/groups';
import { getAdminOrganization } from '@optum/api-client/endpoints/organizations';

import type { RecordRef } from '../../components/shell/record.js';
import {
  RecordActions,
  RecordAsync,
  RecordColumn,
  RecordFacts,
  RecordIdentity,
  RecordOpen,
  RecordRelated,
  RecordSection,
} from '../../components/shell/RecordParts.js';
import { absoluteDate, shortId } from '../../lib/format.js';
import { qk } from '../../lib/query-keys.js';
import { IconTile, Typography } from '../../ui/finixt.js';
import { StatusBadge } from '../../ui/StatusBadge.js';
import { orgStatusLabel } from './org-status.js';

export function OrganizationRecord({ record }: { record: RecordRef }) {
  const navigate = useNavigate();
  const id = record.id;
  const org = useQuery({ queryKey: qk.org(id), queryFn: () => getAdminOrganization(id) });
  const agents = useQuery({
    queryKey: qk.agents(id),
    queryFn: () => listAgents({ organization_id: id }),
  });
  const collections = useQuery({
    queryKey: qk.collections(id),
    queryFn: () => listOrgCollections(id),
  });
  const groups = useQuery({ queryKey: qk.groups(id), queryFn: () => listGroups(id) });
  const count = (n: number | undefined) => (n === undefined ? '…' : String(n));
  const base = `/orgs/${id}`;

  return (
    <RecordAsync query={org} record={record}>
      {(o) => (
        <RecordColumn>
          <RecordIdentity
            avatar={
              <IconTile icon="apartment" size={40} tint="var(--blue-tint)" color="var(--blue)" />
            }
            eyebrow={
              <>
                <Typography variant="eyebrow">Organization</Typography>
                <StatusBadge
                  status={o.status}
                  label={orgStatusLabel(o.status)}
                  vocabulary="organization"
                />
              </>
            }
            name={o.name}
            subtitle={o.description ?? undefined}
          />
          <RecordSection label="Facts">
            <RecordFacts
              items={[
                { label: 'ID', value: shortId(o.id) },
                {
                  label: 'Partner',
                  value: o.federated_partner_id ? shortId(o.federated_partner_id) : '—',
                },
                { label: 'Contact', value: o.contact_email ?? '—', mono: false },
                { label: 'Created', value: absoluteDate(o.created_at) },
                {
                  label: 'Activated',
                  value: o.activated_at ? absoluteDate(o.activated_at) : '—',
                },
                { label: 'Approved by', value: o.approved_by ?? '—', mono: false },
              ]}
            />
          </RecordSection>
          <RecordSection label="In this tenant">
            <RecordRelated
              rows={[
                {
                  icon: 'smart_toy',
                  label: 'Agents',
                  meta: count(agents.data?.length),
                  onClick: () => navigate(`${base}/agents`),
                },
                {
                  icon: 'folder_open',
                  label: 'Collections',
                  meta: count(collections.data?.nodes.length),
                  onClick: () => navigate(`${base}/collections`),
                },
                {
                  icon: 'shield_person',
                  label: 'Groups',
                  meta: count(groups.data?.length),
                  onClick: () => navigate(`${base}/groups`),
                },
                {
                  icon: 'bolt',
                  label: 'Automation',
                  onClick: () => navigate(`${base}/automation`),
                },
              ]}
            />
          </RecordSection>
          <RecordActions>
            <RecordOpen record={record} label="Open workspace" />
          </RecordActions>
        </RecordColumn>
      )}
    </RecordAsync>
  );
}
